import { CropIcon } from '../../../shared/ui/crop-icon/crop-icon';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { LanguageService } from '../../../core/i18n/language.service';
import { FieldWorkTypeService } from '../../../core/services/field-work-type.service';
import { CreateTaskRequest } from '../../../core/services/task.service';
import { FieldWorkType } from '../../../models/field-work-type.model';
import { Field } from '../../../models/field.model';
import {
  CropRotationAdvice,
} from '../../crop-rotation/crop-rotation.model';
import {
  CropRotationService,
} from '../../crop-rotation/crop-rotation.service';

@Component({
  selector: 'app-task-form',
  standalone: true,
  imports: [FormsModule, CropIcon],
  templateUrl: './task-form.html',
  styleUrl: './task-form.scss',
})
export class TaskForm implements OnInit {
  readonly i18n = inject(LanguageService);

  private readonly fieldWorkTypeService =
    inject(FieldWorkTypeService);

  private readonly cropRotationService =
    inject(CropRotationService);

  @Input() fields: Field[] = [];

  @Output() submitted =
    new EventEmitter<CreateTaskRequest>();

  @Output() cancelled =
    new EventEmitter<void>();

  fieldWorkTypes: FieldWorkType[] = [];
  cropCandidates: CropRotationAdvice[] = [];

  fieldId: number | null = null;
  type = '';
  cropId: number | null = null;

  cropLoading = false;
  cropError = false;

  get selectedCropCode(): string | null {
    return this.cropCandidates.find(candidate => candidate.cropId === this.cropId)?.crop ?? null;
  }

  get sowSelected(): boolean {
    return this.type === 'sow';
  }

  get valid(): boolean {
    if (
      this.fieldId === null ||
      this.type === ''
    ) {
      return false;
    }

    if (this.sowSelected) {
      return (
        this.cropId !== null &&
        !this.cropLoading
      );
    }

    return true;
  }

  ngOnInit(): void {
    this.loadFieldWorkTypes();
  }

  fieldChanged(): void {
    this.cropId = null;
    this.cropCandidates = [];
    this.cropError = false;

    if (this.sowSelected) {
      this.loadCropCandidates();
    }
  }

  typeChanged(): void {
    this.cropId = null;
    this.cropCandidates = [];
    this.cropError = false;

    if (this.sowSelected) {
      this.loadCropCandidates();
    }
  }

  submit(): void {
    if (!this.valid) {
      return;
    }

    const field = this.fields.find(
      (item) => item.id === this.fieldId,
    );

    if (!field) {
      return;
    }

    this.submitted.emit({
      fieldId: field.id,
      cropId:
        this.sowSelected
          ? this.cropId
          : null,
      type: this.type,
      title: `${this.type} field ${field.number}`,
      status: 'planned',
      plannedAt: null,
    });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  reset(): void {
    this.fieldId = null;
    this.type =
      this.fieldWorkTypes[0]?.code ?? '';
    this.cropId = null;
    this.cropCandidates = [];
    this.cropLoading = false;
    this.cropError = false;
  }

  taskTypeLabel(type: string): string {
    switch (type) {
      case 'harvest':
        return this.i18n.t('task.harvest');

      case 'plow':
        return this.i18n.t('task.plow');

      case 'lime':
        return this.i18n.t('task.lime');

      case 'cultivate':
        return this.i18n.t('task.cultivate');

      case 'sow':
        return this.i18n.t('task.sow');

      case 'fertilize':
        return this.i18n.t('task.fertilize');

      case 'mulch':
        return this.i18n.t('task.mulch');

      case 'roll':
        return this.i18n.t('task.roll');

      case 'weed':
        return this.i18n.t('task.weed');

      default:
        return this.i18n.t('task.unknown');
    }
  }

  cropLabel(crop: string): string {
    return this.i18n.cropLabel(crop);
  }

  private loadFieldWorkTypes(): void {
    this.fieldWorkTypeService
      .getFieldWorkTypes()
      .subscribe({
        next: (fieldWorkTypes) => {
          this.fieldWorkTypes =
            fieldWorkTypes;

          if (
            this.type === '' &&
            fieldWorkTypes.length > 0
          ) {
            this.type =
              fieldWorkTypes[0].code;
          }
        },
      });
  }

  private loadCropCandidates(): void {
    if (this.fieldId === null) {
      return;
    }

    this.cropLoading = true;
    this.cropError = false;

    this.cropRotationService
      .getAdvice(this.fieldId, 1)
      .subscribe({
        next: (response) => {
          this.cropCandidates =
            response.candidates;

          this.cropLoading = false;
        },
        error: () => {
          this.cropCandidates = [];
          this.cropId = null;
          this.cropLoading = false;
          this.cropError = true;
        },
      });
  }
}
